/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { Label } from "reactstrap";
import { secondsToTimeString, unixEpochToString } from "../../util/DateTime";
import ItemLink from "../../elements/ItemLink";
import ActionButton from "../../elements/ActionButton";
import CenterModal from "../../elements/CenterModal";
import SortableTable from "../../elements/SortableTable";

const ModalItemList = ({
  type,
  isOpen,
  items,
  onClick,
  onDone
}) => {
  // helpers
  let title, icon;
  switch (type) {
    case "client": title = "Business List"; icon = "briefcase-fill"; break;
    case "location": title = "Location List"; icon = "building-fill"; break;
    case "router": title = "Router List"; icon = "router-fill"; break;
    case "marketing": title = "Marketing Campaigns"; icon = "megaphone-fill"; break;
    case "brand": title = "White Label Brands"; icon = "building-fill"; break;
    default: return null;
  };

  const itemLinkProps = {
    className: "no-spacing",
    type: type,
    onClick: (item) => {
      onClick?.(item);
      onDone?.();
    },
    showIcon: true,
  };

  const getMarketingType = (item) => {
    switch (item) {
      case "broadcast": return <span><i className="bi-send-fill" /> Broadcast</span>;
      case "automation": return <span><i className="bi-repeat" /> Automation</span>;
      default: return <span className="text-muted">Unknown</span>;
    }
  };
  const getMarketingMedium = (item) => {
    switch (item) {
      case "email": return <span><i className="bi-envelope-fill" /> Email</span>;
      case "sms": return <span><i className="bi-chat-dots-fill" /> SMS</span>;
      default: return <span className="text-muted">Unknown</span>;
    }
  };

  // booleans
  const isClient = type === "client";
  const isRouter = type === "router";
  const isMarketing = type === "marketing";

  // table columns
  const renderTableLabel = ({ index, data, id, value }) => {
    const rawValue = data[id];
    let innerElement;
    switch (id) {
      case "macAddress":
      case "name":
        innerElement = <ItemLink {...itemLinkProps} {...data.itemLink}>{value}</ItemLink>;
        break;
      case "clientName":
        innerElement = (
          <ItemLink
            {...itemLinkProps}
            {...data.itemLink}
            type="client"
            item={{ id: data.clientId, name: data.clientName }}
          >
            {value}
          </ItemLink>
        );
        break;
      case "marketingType": innerElement = getMarketingType(rawValue); break;
      case "medium": innerElement = getMarketingMedium(rawValue); break;
      case "lastActive": innerElement = `${secondsToTimeString(value)} ago`; break;
      case "createdAt": innerElement = unixEpochToString(value, false); break;
      default: innerElement = value; break;
    }

    return <td key={index} style={{ wordWrap: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}>{innerElement}</td>;
  };

  const marketingTypeEnum = { broadcast: "Broadcast", automation: "Automation" };
  const marketingMediumEnum = { email: "Email", sms: "SMS" };
  const columns = [];

  if (isRouter) {
    columns.push({ id: "macAddress", name: "MAC Address", type: "string", render: renderTableLabel });
  } else {
    columns.push({ id: "name", name: "Name", type: "string", render: renderTableLabel });
  }
  if (!isClient) {
    columns.push({ id: "clientName", name: "Business", type: "string", render: renderTableLabel });
  } else {
    columns.push({ id: "brandTitle", name: "White Label", type: "string", render: renderTableLabel })
  }
  if (isMarketing) {
    columns.push({ id: "marketingType", name: "Type", type: "enum", enum: marketingTypeEnum, render: renderTableLabel });
    columns.push({ id: "medium", name: "Medium", type: "enum", enum: marketingMediumEnum, render: renderTableLabel });
  } else {
    columns.push({ id: "lastActive", name: "Last Active", type: "number", render: renderTableLabel });
  }
  columns.push({ id: "createdAt", name: "Created At", type: "number", render: renderTableLabel });

  // parse items
  const data = items.map(item => ({
    ...item,
    itemLink: {
      ...(type === "client") && { skipAction: onClick != null },
      item: item
    }
  }));

  return (
    <CenterModal isOpen={isOpen} toggle={onDone} size="lg">
      <div className="fill-container d-flex flex-column">
        <div className="flex-static d-flex justify-content-between">
          <div style={{ width: "28px" }} />
          <Label className="pb-2 text-center" tag="h3">
            <i className={`pe-2 icon bi bi-${icon}`}/>
            {title}
          </Label>
          <ActionButton type="close" onClick={onDone} />
        </div>
        <div className="flex-dynamic d-flex" style={{ height: "80vh" }}>
          <SortableTable
            data={data}
            columns={columns}
            initialSortKey="createdAt"
            placeholder="No items found..."
          />
        </div>
      </div>
    </CenterModal>
  );
};

export default ModalItemList;
